/**
 * SmartLink auto-rank — dash data-layer client.
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/smartlink-autorank` surface (backend feature #110). Mirrors the
 * contract declared in `apps/api/src/routes/smartlink-autorank.ts`:
 *
 *   PUT  /stats                        — upsert per-offer rolling stats
 *   GET  /stats/:offer_id              — single-row fetch, zero-filled on miss
 *   POST /rank                         — rank a batch by composite score
 *
 * No React / TanStack deps in this file — the helpers are reused from the
 * page component's tests without dragging the render surface along.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Weight shape — UI surfaces four knobs (epc, cr, payout, history). Matches
// the backend's `RankWeights`. Defaults mirror the server defaults
// (40 / 30 / 20 / 10) and are expressed as integer percentages in the UI for
// ergonomics; the wire format is floats summing to ~1.
// ---------------------------------------------------------------------------

export const WEIGHT_KEYS = ["epc", "cr", "payout", "history"] as const;
export type WeightKey = (typeof WEIGHT_KEYS)[number];

export interface WeightPercents {
	epc: number;
	cr: number;
	payout: number;
	history: number;
}

export const DEFAULT_WEIGHT_PERCENTS: WeightPercents = {
	epc: 40,
	cr: 30,
	payout: 20,
	history: 10,
};

export const WEIGHT_LABELS: Record<WeightKey, string> = {
	epc: "EPC",
	cr: "CR",
	payout: "Payout",
	history: "History",
};

export const WEIGHT_HINTS: Record<WeightKey, string> = {
	epc: "Earnings per click over the window.",
	cr: "Conversion rate in basis points.",
	payout: "Gross payout per conversion.",
	history: "Total click volume (log-scaled).",
};

// ---------------------------------------------------------------------------
// Stats shape — matches the API row verbatim. The stats editor only touches
// the four primary numeric fields plus offer_id; `window_start`/`window_end`
// are server-computed from the current wall clock.
// ---------------------------------------------------------------------------

export interface OfferStatsRow {
	offer_id: string;
	tenant_id: string;
	epc_cents: number;
	cr_bps: number;
	payout_cents: number;
	history_clicks: number;
	window_start: number;
	window_end: number;
	updated_at: number;
}

export interface OfferStatsInput {
	offer_id: string;
	epc_cents: number;
	cr_bps: number;
	payout_cents: number;
	history_clicks: number;
}

export interface RankedOffer {
	offer_id: string;
	score: number;
	epc_cents: number;
	cr_bps: number;
	payout_cents: number;
	history_clicks: number;
}

export interface RankResponse {
	tenant_id: string;
	weights: { epc: number; cr: number; payout: number; history: number };
	offers: RankedOffer[];
}

// ---------------------------------------------------------------------------
// Validation — hand-rolled safeParse surface. Keeps the feature zero-dep on
// zod so the lane stays narrow. Offer id must be a non-empty trimmed string;
// every numeric field must be a finite integer >= 0.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface StatsFormInput {
	offer_id: string;
	epc_cents: string | number;
	cr_bps: string | number;
	payout_cents: string | number;
	history_clicks: string | number;
}

const NUMERIC_FIELDS = [
	"epc_cents",
	"cr_bps",
	"payout_cents",
	"history_clicks",
] as const;

const OFFER_ID_MAX = 120;

function parseNonNegInt(raw: string | number): {
	ok: boolean;
	value: number;
	msg?: string;
} {
	if (typeof raw === "number") {
		if (!Number.isFinite(raw)) {
			return { ok: false, value: 0, msg: "Must be a finite number." };
		}
		if (!Number.isInteger(raw)) {
			return { ok: false, value: 0, msg: "Must be an integer." };
		}
		if (raw < 0) {
			return { ok: false, value: 0, msg: "Must be zero or positive." };
		}
		return { ok: true, value: raw };
	}
	const trimmed = raw.trim();
	if (trimmed.length === 0) {
		return { ok: false, value: 0, msg: "Required." };
	}
	if (!/^\d+$/.test(trimmed)) {
		return { ok: false, value: 0, msg: "Must be an integer >= 0." };
	}
	const n = Number.parseInt(trimmed, 10);
	if (!Number.isFinite(n)) {
		return { ok: false, value: 0, msg: "Must be a finite integer." };
	}
	return { ok: true, value: n };
}

export const statsFormSchema = {
	safeParse(input: StatsFormInput): ParseResult<OfferStatsInput> {
		const errors: Record<string, string> = {};
		const offerId = (input.offer_id ?? "").trim();
		if (offerId.length === 0) {
			errors.offer_id = "Offer ID is required.";
		} else if (offerId.length > OFFER_ID_MAX) {
			errors.offer_id = `Offer ID must be ${OFFER_ID_MAX} chars or fewer.`;
		} else if (/[\x00-\x1f]/.test(offerId)) {
			errors.offer_id = "Offer ID contains invalid control characters.";
		}

		const parsed: Partial<OfferStatsInput> = { offer_id: offerId };
		for (const field of NUMERIC_FIELDS) {
			const result = parseNonNegInt(input[field]);
			if (!result.ok) {
				errors[field] = result.msg ?? "Invalid value.";
			} else {
				parsed[field] = result.value;
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return {
			success: true,
			data: parsed as OfferStatsInput,
		};
	},
};

// ---------------------------------------------------------------------------
// Weight normalization — UI allows 0..100 integer sliders per key. The wire
// payload needs floats summing to ~1 (the backend tolerates any non-negative
// weights; scale-invariant due to z-score blending). We normalize here so
// the rank preview reflects the operator's relative intent exactly.
// ---------------------------------------------------------------------------

export interface NormalizedWeights {
	epc: number;
	cr: number;
	payout: number;
	history: number;
}

export function sumWeights(w: WeightPercents): number {
	return w.epc + w.cr + w.payout + w.history;
}

/**
 * Returns weights normalized to sum to 1. When the raw sum is <= 0 (all
 * sliders at zero), falls back to an equal split so the /rank call still
 * succeeds deterministically.
 */
export function normalizeWeights(w: WeightPercents): NormalizedWeights {
	const total = sumWeights(w);
	if (total <= 0) {
		return { epc: 0.25, cr: 0.25, payout: 0.25, history: 0.25 };
	}
	return {
		epc: w.epc / total,
		cr: w.cr / total,
		payout: w.payout / total,
		history: w.history / total,
	};
}

/**
 * Clamp a slider value into the 0..100 integer range so free-text input
 * never poisons the normalizer.
 */
export function clampWeightPercent(raw: number): number {
	if (!Number.isFinite(raw)) return 0;
	const int = Math.round(raw);
	if (int < 0) return 0;
	if (int > 100) return 100;
	return int;
}

// ---------------------------------------------------------------------------
// Offer-id list parsing — textarea input, one id per line or comma-delimited.
// Preserves operator order and strips empties + duplicates.
// ---------------------------------------------------------------------------

export function parseOfferIdList(raw: string): string[] {
	const parts = raw.split(/[\n,]/);
	const seen = new Set<string>();
	const out: string[] = [];
	for (const part of parts) {
		const trimmed = part.trim();
		if (trimmed.length === 0) continue;
		if (seen.has(trimmed)) continue;
		seen.add(trimmed);
		out.push(trimmed);
	}
	return out;
}

// ---------------------------------------------------------------------------
// Remote operations.
// ---------------------------------------------------------------------------

const BASE = "/smartlink-autorank";

/**
 * Upsert a single offer's rolling stats. Backend zero-floors negative values
 * but we validate before sending so operators see immediate feedback.
 */
export async function putOfferStats(
	input: OfferStatsInput,
): Promise<OfferStatsRow> {
	const res = await apiClient.put<{ ok: boolean; row: OfferStatsRow }>(
		`${BASE}/stats`,
		input,
	);
	return res.row;
}

/**
 * Fetch a single offer's stats row. Backend returns a zero row on miss so
 * the table is never empty once an offer id is entered.
 */
export async function getOfferStats(offerId: string): Promise<OfferStatsRow> {
	try {
		return await apiClient.get<OfferStatsRow>(
			`${BASE}/stats/${encodeURIComponent(offerId)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) {
			return {
				offer_id: offerId,
				tenant_id: "",
				epc_cents: 0,
				cr_bps: 0,
				payout_cents: 0,
				history_clicks: 0,
				window_start: 0,
				window_end: 0,
				updated_at: 0,
			};
		}
		throw err;
	}
}

export interface RankRequest {
	offer_ids: string[];
	weights: NormalizedWeights;
}

export async function rankOffers(req: RankRequest): Promise<RankResponse> {
	return apiClient.post<RankResponse>(`${BASE}/rank`, req);
}

// ---------------------------------------------------------------------------
// TanStack Query keys — colocated so the page can invalidate precisely.
// ---------------------------------------------------------------------------

export const autorankQueryKeys = {
	all: ["smartlinks-autorank"] as const,
	stats: (offerId: string) =>
		[...autorankQueryKeys.all, "stats", offerId] as const,
	rank: (fingerprint: string) =>
		[...autorankQueryKeys.all, "rank", fingerprint] as const,
};
